// ARCA-121 Bug B — staged-word chip in the Match control-deck.
//
// After a word trace is staged (pointer-up commits $turnBuilder.phaseA), the
// player needs a persistent, near-the-Submit-button confirmation of WHICH word
// they staged and what it will score. The floating ScorePill sits at the TOP of
// the board (and the bug report — runegambit#110 — found the live pill vanishing
// on lift); this compact readout lives directly above the Submit button so the
// staged word + projected reward are visible where the player is about to tap.
//
// It mirrors the tutorial's "Spelled CAT · …" affordance but shows the REAL
// projected score + token band via buildScorePillBreakdown — the SAME pure
// formatter the ScorePill uses, so the two surfaces can never diverge. DEC-021:
// the server is authoritative on the actual turn reward; this is a live
// client-side preview only.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PlayerView } from '../../game/PlayerViewSchema'
import { buildScorePillBreakdown } from './ScorePillFormat'

export interface StagedWordChipProps {
  /** The staged word string (`$turnBuilder.phaseA.word`). */
  readonly word: string
  /** The staged cell path (`$turnBuilder.phaseA.path`) — drives the score. */
  readonly path: ReadonlyArray<number>
  readonly playerView: PlayerView
  readonly callerSlot: 0 | 1
}

/**
 * Renders nothing until a word is actually staged (empty word / empty path →
 * null), so the chip only appears once the player has traced something.
 */
export function StagedWordChip({
  word,
  path,
  playerView,
  callerSlot,
}: StagedWordChipProps): JSX.Element | null {
  const intl = useIntl()
  if (word.length === 0 || path.length === 0) return null

  // Reuse the ScorePill's pure breakdown for the projected total + token band
  // so the chip and the floating pill stay in lock-step.
  const { total, tokens } = buildScorePillBreakdown(
    path,
    playerView,
    callerSlot,
    intl,
  )

  return (
    <div
      data-screen-region="staged-word-chip"
      // Surfaced for e2e: the staged word + its projected score, observable
      // before the player taps Submit.
      data-staged-word={word.toUpperCase()}
      data-staged-score={total}
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '0 16px',
        marginBottom: 4,
        fontFamily: 'var(--font-family-display)',
        fontWeight: 700,
        fontSize: 'var(--font-size-16)',
        color: 'var(--color-player-a)',
        textAlign: 'center',
      }}
    >
      <FormattedMessage
        id="match.staged_word.readout"
        defaultMessage="Spelled {word} · {score} pts · +{tokens, plural, one {# token} other {# tokens}}"
        values={{ word: word.toUpperCase(), score: total, tokens }}
      />
    </div>
  )
}
